import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWebSocket } from '../../contexts/WebSocketContext';
import ActiveRoomsDropdown from './ActiveRoomsDropdown';
import './FreePlay.css';

const MODES = [
  {
    id: 'deal',
    title: 'Deal Rummy',
    desc: 'Fixed deals — lowest total score wins',
    icon: '🎯',
    defaults: { totalDeals: 2, maxPlayers: 2 },
    settings: [
      { key: 'totalDeals', label: 'Number of Deals', options: [2, 4] },
      { key: 'maxPlayers', label: 'Players', options: [2, 4, 6] },
    ]
  },
  {
    id: 'points',
    title: 'Pool Rummy',
    desc: 'Exceed target points = eliminated. Last player standing wins!',
    icon: '🏆',
    defaults: { maxPoints: 201, maxPlayers: 6 },
    settings: [
      { key: 'maxPlayers', label: 'Players', options: [2, 4, 6] },
      { key: 'maxPoints', label: 'Target Points', options: [101, 201, 301] },
    ]
  }
];

const FreePlayGameSelection = () => {
  const navigate = useNavigate();
  const { isConnected, joinFreePlayWithOptions, socket } = useWebSocket();

  const [selectedMode, setSelectedMode] = useState(null);
  const [config, setConfig] = useState({});
  const [isJoining, setIsJoining] = useState(false);
  const [error, setError] = useState('');

  const handleModeSelect = (modeId) => {
    const mode = MODES.find(m => m.id === modeId);
    setSelectedMode(modeId);
    setConfig({ ...mode.defaults });
    setError('');
  };

  const updateSetting = (key, value) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  };

  // Navigate directly io the persistent game room & table
  const openGameWindow = (result) => {
    const gameId = result.gameId || result.roomCode;
    const url = `/game/${gameId}`;
    const navState = {
      roomCode: result.roomCode,
      gameId: result.gameId,
      seatPosition: result.seatPosition,
      playerCount: result.playerCount,
      maxPlayers: result.maxPlayers || config.maxPlayers,
      gameMode: selectedMode,
      isFreePlay: true,
      ...config
    };
    navigate(url, { state: navState });
  };

  const joinFreePlay = useCallback(async () => {
    if (!isConnected || !socket) {
      setError('Not connected to server.');
      return;
    }
    if (!selectedMode) {
      setError('Please select a game mode.');
      return;
    }

    setIsJoining(true);
    setError('');

    try {
      const options = { gameMode: selectedMode, ...config };
      const result = await joinFreePlayWithOptions(options);

      if (result && result.success && result.roomCode) {
        openGameWindow(result);
      } else {
        setError(result?.message || 'Failed to join room.');
      }
    } catch (err) {
      setError(err.message || 'Failed to join free play.');
    } finally {
      setIsJoining(false);
    }
  }, [isConnected, socket, selectedMode, config, joinFreePlayWithOptions, navigate]);

  // Join a specific room picked from the active-rooms dropdown.
  const joinSpecificRoom = useCallback(async (room) => {
    if (!isConnected || !socket) {
      setError('Not connected to server.');
      return;
    }
    setIsJoining(true);
    setError('');
    try {
      const result = await joinFreePlayWithOptions({ preferredRoomCode: room.roomCode });
      if (result && result.success && result.roomCode) {
        const gameId = result.gameId || result.roomCode;
        navigate(`/game/${gameId}`, {
          state: {
            roomCode: result.roomCode,
            gameId: result.gameId,
            seatPosition: result.seatPosition,
            playerCount: result.playerCount,
            maxPlayers: result.maxPlayers || room.maxPlayers,
            gameMode: room.gameMode,
            isFreePlay: true,
            ...(room.gameMode === 'deal' ? { totalDeals: room.deals } : { maxPoints: room.points })
          }
        });
      } else {
        setError(result?.message || 'Failed to join room.');
      }
    } catch (err) {
      setError(err.message || 'Failed to join room.');
    } finally {
      setIsJoining(false);
    }
  }, [isConnected, socket, joinFreePlayWithOptions, navigate]);

  const selectedModeData = MODES.find(m => m.id === selectedMode);

  return (
    <div className="fs-page">
      {/* Minimal Header */}
      <div className="fs-header">
        {/* Step 2 -> back to the game list; step 1 -> back to the lobby */}
        <button className="fs-back" onClick={() => (selectedMode ? setSelectedMode(null) : navigate('/multiplayer'))}>
          ← Back
        </button>
        <h1>{selectedModeData ? `Free Play · ${selectedModeData.title}` : 'Free Play'}</h1>
        <span className={`fs-status ${isConnected ? 'connected' : ''}`}>
          {isConnected ? '● Connected' : '○ Disconnected'}
        </span>
      </div>

      {error && (
        <div className="fs-error">
          <span>{error}</span>
          <button onClick={() => setError('')}>✕</button>
        </div>
      )}

      {/* STEP 2 only: this game's rooms */}
      {selectedMode && <ActiveRoomsDropdown kind="freeplay" onJoin={joinSpecificRoom} gameMode={selectedMode} />}

      {/* STEP 1: choose the game - the options below appear once one is opened */}
      {!selectedMode && (
        <div className="fs-modes">
          {MODES.map(mode => (
            <div
              key={mode.id}
              className="fs-mode-card"
              onClick={() => handleModeSelect(mode.id)}
            >
              <span className="fs-mode-icon">{mode.icon}</span>
              <div className="fs-mode-text">
                <h3>{mode.title}</h3>
                <p>{mode.desc}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Settings - Only show when mode selected */}
      {selectedModeData && (
        <div className="fs-settings">
          {selectedModeData.settings.map(setting => (
            <div key={setting.key} className="fs-setting-row">
              <label>{setting.label}</label>
              <div className="fs-options">
                {setting.options.map(opt => (
                  <button
                    key={opt}
                    className={`fs-opt-btn ${config[setting.key] === opt ? 'active' : ''}`}
                    onClick={() => updateSetting(setting.key, opt)}
                  >
                    {setting.key === 'maxPoints' ? `${opt} Pts` : `${opt}`}
                  </button>
                ))}
              </div>
            </div>
          ))}

          {/* Join Button */}
          <button
            className={`fs-join-btn ${!isConnected || isJoining ? 'disabled' : ''}`}
            onClick={joinFreePlay}
            disabled={!isConnected || isJoining}
          >
            {isJoining ? (
              <><span className="fs-spinner" /> Joining...</>
            ) : (
              `Join Game`
            )}
          </button>
        </div>
      )}

      {/* Quick Info */}
      {selectedModeData && (
        <div className="fs-info">
          <span>⚡ Waits for matching players · Auto-starts when ready</span>
        </div>
      )}
    </div>
  );
};

export default FreePlayGameSelection;
